import XRegExp from "xregexp";

import { swSettings } from "app/common/services/swSettings";
import type { SegmentId } from "app/constants/MmxChannels";
import { openDefaultHooksModal } from "app/services/ModalService";
import { PreferencesService } from "app/services/preferences/preferencesService";
import type {
    ICustomSegment,
    ICustomSegmentGroupWebsite,
    ICustomSegmentsGroup,
    ICustomSegmentsMetaData,
    ISegmentsModule,
} from "app/services/segments/segmentsApiService";
import { EMPTY_CUSTOM_SEGMENTS, SEGMENT_KEY_SEPARATOR, SEGMENT_TYPES } from "app/services/segments/segmentsApiService";
import {
    escapeRegexString,
    KEYWORDS_DELIMITER_REGEX_PATTERN,
} from "app/services/segments/segmentsProcessor/segmentsKeywordsVariables";
import type { ISegmentRuleItem, IUrlSegments } from "app/services/segments/segmentsWizardServiceTypes";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import type { IRule } from "pro-features/components/RulesQueryBuilder/src/RulesQueryBuilderTypes";

export const getHasSegmentShareClaim = () =>
    swSettings?.components?.CustomSegments?.resources?.CustomSegmentsShareability;
const SEGMENT_ANALYSIS_PREFERENCES_KEY = `segmentAnalysis`;
const getDefaultPriorityCountryFilter = () => [
    String(swSettings.components.CustomSegments.resources.InitialCountry),
    "840",
];

export type ICustomSegmentMember = ICustomSegment | ICustomSegmentGroupWebsite;
export interface ICustomSegmentAvailableMembers {
    segments?: ICustomSegment[];
    websites?: ICustomSegmentGroupWebsite[];
}

const sortSegmentsByDomainName = (a: ICustomSegment, b: ICustomSegment) =>
    a.domain > b.domain
        ? 1
        : a.domain < b.domain // sort by domain asc
          ? -1
          : (a.segmentName ?? "").toLowerCase() > (b.segmentName ?? "").toLowerCase() // if same domain, sort by name asc
            ? 1
            : -1;
const sortSegmentsByCreationDomain = (a: ICustomSegment, b: ICustomSegment) =>
    // @ts-ignore FIXME: 'a.creationDate' and 'b.creationDate' is possibly 'undefined'
    a.creationDate > b.creationDate
        ? -1
        : // @ts-ignore FIXME: 'a.creationDate' and 'b.creationDate' is possibly 'undefined'
          a.creationDate < b.creationDate // sort by creationDate desc
          ? 1
          : sortSegmentsByDomainName(a, b); // if same creationDate, sort by domain asc
const sortSegmentsGroupsByName = (a: ICustomSegmentsGroup, b: ICustomSegmentsGroup) => (a.name > b.name ? 1 : -1); // sort by name desc
const sortSegmentsGroupsByCreationName = (a: ICustomSegmentsGroup, b: ICustomSegmentsGroup) =>
    // @ts-ignore FIXME: 'a.creationTime' and 'b.creationTime' is possibly 'undefined'
    a.creationTime > b.creationTime
        ? -1
        : // @ts-ignore FIXME: 'a.creationTime' and 'b.creationTime' is possibly 'undefined'
          a.creationTime < b.creationTime // sort by creationTime desc
          ? 1
          : sortSegmentsGroupsByName(a, b); // if same creationTime, sort by domain asc

const matchSegmentByQuery = (segment: ICustomSegment, query: string) => {
    const queryLowered = query.toLowerCase();
    return (
        segment.domain?.toLowerCase().includes(queryLowered) ||
        segment.segmentName?.toLowerCase().includes(queryLowered)
    );
};
const matchSegmentsGroupsByQuery = (group: ICustomSegmentsGroup, query: string) => {
    const queryLowered = query.toLowerCase();
    return group.name?.toLowerCase().includes(queryLowered);
};

export const SegmentsUtils = {
    isMidTierUser: () => {
        try {
            return (
                swSettings.components.CustomSegments.resources.IsAdvancedUser &&
                !swSettings.components.CustomSegments.resources.IsSegmentFullAccessTrial
            );
        } catch (e) {
            return false;
        }
    },
    getSegmentGroupById: (segmentGroups: ICustomSegmentsGroup[], groupId: string): ICustomSegmentsGroup | undefined => {
        return segmentGroups.find((group) => group?.id === groupId);
    },
    getSegmentById: (
        { segments = [] }: ICustomSegmentAvailableMembers,
        segmentId: SegmentId,
    ): ICustomSegment | undefined => {
        return segments.find((segment) => segment?.id === segmentId);
    },
    getSegmentWebsiteByDomain: (
        { websites = [] }: ICustomSegmentAvailableMembers,
        domain: string,
    ): ICustomSegmentGroupWebsite | undefined => websites.find((segWebsite) => segWebsite.domain === domain),

    getSegmentIdTypeByKey: (segmentKey: string): [string, SEGMENT_TYPES] => {
        const segmentKeyParts = segmentKey.split(SEGMENT_KEY_SEPARATOR, 2);
        let segmentId, segmentType;
        if (segmentKeyParts.length < 2) {
            segmentType = SEGMENT_TYPES.SEGMENT;
            segmentId = segmentKeyParts[0];
        } else {
            // @ts-ignore FIXME: segmentKeyParts[0] - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'typeof SEGMENT_TYPES'
            segmentType = SEGMENT_TYPES[segmentKeyParts[0]];
            segmentId = segmentKeyParts[1];
        }
        return [segmentId, segmentType];
    },

    getSegmentObjectByKey: <T extends ICustomSegmentMember>(
        segmentKey: SegmentId,
        availableMembers: ICustomSegmentAvailableMembers,
    ): [T, SEGMENT_TYPES] => {
        const [segmentId, segmentType] = SegmentsUtils.getSegmentIdTypeByKey(segmentKey);
        let segmentObj;
        switch (segmentType) {
            case SEGMENT_TYPES.SEGMENT:
                segmentObj = SegmentsUtils.getSegmentById(availableMembers, segmentId);
                break;
            case SEGMENT_TYPES.WEBSITE:
                segmentObj = SegmentsUtils.getSegmentWebsiteByDomain(availableMembers, segmentId);
                break;
        }
        return [segmentObj as T, segmentType];
    },
    getSegmentObjectsByKeys: (
        segmentKeys: string[],
        { segments = [], websites = [] }: ICustomSegmentAvailableMembers,
    ): [ICustomSegmentMember, SEGMENT_TYPES][] => {
        const segmentsDataMap = segments.reduce<Record<string, ICustomSegment>>((acc, cur) => {
            acc[cur.id] = cur;
            return acc;
        }, {});
        const websitesDataMap = websites.reduce<Record<string, ICustomSegmentGroupWebsite>>((acc, cur) => {
            acc[cur.domain] = cur;
            return acc;
        }, {});
        return segmentKeys.map((segKey) => {
            const [segId, segType] = SegmentsUtils.getSegmentIdTypeByKey(segKey);
            let segObj;
            switch (segType) {
                case SEGMENT_TYPES.SEGMENT:
                    segObj = segmentsDataMap[segId];
                    break;
                case SEGMENT_TYPES.WEBSITE:
                    segObj = websitesDataMap[segId];
                    break;
            }
            return [segObj, segType];
        });
    },

    getRulesString: (rules: IRule[]): string => {
        return rules?.reduce((acc, item, idx) => {
            const allRules = item.words?.concat(item.exact).concat(item.folders).concat(item.exactURLS);
            acc +=
                allRules.length > 0
                    ? `${idx > 0 ? (item.type === 1 ? " NOT " : " AND ") : ""}(${allRules.join(", ")})`
                    : "";
            return acc;
        }, "");
    },

    // User countries are read from Conversion settings (sub-settings of Conversion, as ConversionOverview do not have filtered countries)
    getUserCountries: (): string[] => {
        if (!swSettings.components.CustomSegments.resources.Countries) {
            return [];
        }
        return Array.isArray(swSettings.components.CustomSegments.resources.Countries)
            ? swSettings.components.CustomSegments.resources.Countries
            : [swSettings.components.CustomSegments.resources.Countries];
    },

    getDefaultUserCountry: (userCountryFilter?: string[], priorityCountryFilter?: string[]) => {
        const userCountries = userCountryFilter ?? SegmentsUtils.getUserCountries();
        const priorityCountries = priorityCountryFilter ?? getDefaultPriorityCountryFilter();
        const userPriorityCountryCode = priorityCountries.find((priorityCountry) =>
            // @ts-ignore FIXME: Argument of type 'number' is not assignable to parameter of type 'string'
            userCountries.includes(+priorityCountry),
        );
        return userPriorityCountryCode ?? (userCountries[0] ? String(userCountries[0]) : undefined);
    },

    getDefaultFiltersParams: () => ({
        duration: "6m",
        country: SegmentsUtils.getDefaultUserCountry() || "840",
    }),

    getSegmentsAnalysisPrefKey: (pageId?: string) => {
        return SEGMENT_ANALYSIS_PREFERENCES_KEY + (pageId ? ":" + pageId : "");
    },

    getPageFilterParams: (pageId?: string): Record<string, FixmeType> => {
        const availableCountryCodes = SegmentsUtils.getUserCountries();
        const segAnalysisPrefKey = SegmentsUtils.getSegmentsAnalysisPrefKey(pageId);
        const pref: { country: string } = PreferencesService.get(segAnalysisPrefKey);
        const defaultFilterParams = SegmentsUtils.getDefaultFiltersParams();
        return {
            ...defaultFilterParams,
            ...pref,
            country:
                pref && availableCountryCodes.indexOf(`${Number.parseInt(pref.country, 10)}`) !== -1
                    ? pref.country
                    : defaultFilterParams.country,
        };
    },

    openMidTierUserUpsellModal: () => {
        openDefaultHooksModal("segments", LocationService.getCurrentLocationTracking());
    },

    sortSegments: {
        byDomainName: sortSegmentsByDomainName,
        byCreationDomain: sortSegmentsByCreationDomain,
    },
    matchSegments: {
        byQuery: matchSegmentByQuery,
    },

    sortSegmentsGroups: {
        byName: sortSegmentsGroupsByName,
        byCreationName: sortSegmentsGroupsByCreationName,
    },
    matchSegmentsGroups: {
        byQuery: matchSegmentsGroupsByQuery,
    },
    matchUrlByRules: (checkUrl: string, checkRules: ISegmentRuleItem[], domain: string): boolean => {
        // if no rules to check, return all the data (whether filtered or all)
        if (checkRules.length === 0) {
            return true;
        }
        const delimitersRegex = XRegExp(`(?:${KEYWORDS_DELIMITER_REGEX_PATTERN})+`, "ui");

        const deriveKeywordsFromUrl = (url: string, domain: string): Set<string> => {
            const urlWithoutDomain = url.toLowerCase().replace(domain, "");
            const keywordsSet = new Set(urlWithoutDomain.split(delimitersRegex));
            keywordsSet.delete("");
            return keywordsSet;
        };
        // check whether any word of a rule exists in entry segments
        const checkAnyWordInSegments = (words: string[], segments: Set<string>): boolean =>
            words.some((w) => segments.has(w));

        // Check whether any exact-phrase of a rule is contained within a url
        const checkAnyExactInUrl = (exact: string[] | undefined, url: string) =>
            exact?.some((e) => {
                // fix for JIRA SIM-28279 - we want our search to be case insensitive
                const exactRegex = new RegExp(`${escapeRegexString(e)}`, "gi");
                return exactRegex.test(url);
            }) ?? false;

        const checkAnyExactUrls = (exact: string[] | undefined, url: string) =>
            exact?.some((e) => {
                // fix for JIRA SIM-28279 - we want our search to be case insensitive
                const exactRegex = new RegExp(`^${escapeRegexString(e)}[\\/,?]?(?![\\/a-zA-Z0-9])`, "gi");
                return exactRegex.test(url);
            }) ?? false;

        const checkAnyFolderInUrl = (folders: string[] | undefined, url: string) =>
            folders?.some(
                (f) =>
                    url.toLowerCase().startsWith(f.toLowerCase()) && ["/", "?", "#", undefined].includes(url[f.length]),
            );

        const checkUrlObj = {
            URL: checkUrl,
            Segments: deriveKeywordsFromUrl(checkUrl, domain),
        };

        // create check function according to rules to check whether a popular page entry fulfills the rules
        const check = (entry: IUrlSegments) =>
            checkRules.every((rule) => {
                // an entry is considered a "match" in case the current rule has a word (or more) within its segments
                // or in case the current rule has an exact phrase (or more) within its url
                const hasMatch =
                    checkAnyWordInSegments(rule.words, entry.Segments) ||
                    checkAnyExactInUrl(rule.exact, entry.URL) ||
                    checkAnyFolderInUrl(rule.folders, entry.URL) ||
                    checkAnyExactUrls(rule.exactURLS, entry.URL);

                return rule.type === 0
                    ? hasMatch // include any word or exact phrase
                    : !hasMatch; // exclude any word or exact phrase
            });

        return check(checkUrlObj);
    },
    doSegmentsExistInAccount: (customSegmentsMeta: ICustomSegmentsMetaData) =>
        customSegmentsMeta.Segments.length > 0 ||
        customSegmentsMeta.SegmentGroups.length > 0 ||
        customSegmentsMeta.AccountSegments.length > 0,
    doSegmentsExistInUserData: () => {
        const preferences = PreferencesService.get();
        return Object.keys(preferences).some((key) => key.startsWith(SEGMENT_ANALYSIS_PREFERENCES_KEY));
    },
    getUserAndAccountSegmentByIdHof: (
        segmentsModule: ISegmentsModule = {
            segmentsLoading: false,
            customSegmentsMeta: EMPTY_CUSTOM_SEGMENTS,
        },
    ) => {
        const { customSegmentsMeta = EMPTY_CUSTOM_SEGMENTS } = segmentsModule;
        const { Segments: userSegments = [], AccountSegments: accountSegments = [] } = customSegmentsMeta;
        const allSegments: Record<string, ICustomSegment> = {};
        [...userSegments, ...accountSegments].forEach((segment) => {
            allSegments[segment.id] = segment;
        });
        return (segmentId: string) => {
            return allSegments[segmentId];
        };
    },
};
